import { useAiChat } from "@/hooks/useAiChat";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { AiChatMessageRow } from "@/overlay/components/AiChatWindow/AiChatMessageRow"; // reuse row for styling
import { useWorkspaceAiContext } from "@/overlay/components/AiChatWindow/useWorkspaceAIContext";
import { useAppDispatch } from "@/reducer";
import type { RootState } from "@/reducer";
import { setAiChatRecording } from "@/reducer/UIState";
import type { AiChatMessage } from "@/types/ai-chat";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { ActionCreators } from "redux-undo";
import "./style.scss";

type RevertState = "idle" | "reverting" | "done" | "error";

const TOAST_AUTO_HIDE_MS = 4000;
const TOAST_FADE_MS = 250;
const TOAST_HOVER_HIDE_DELAY_MS = 2000;

export function AmbientAiChatListener() {
  const workspaceContext = useWorkspaceAiContext();
  const { isWindowVisible } = useDesktop();
  const chatVisible = isWindowVisible(instanceId(WindowKind.AiChatWindow));
  const dispatch = useAppDispatch();
  const pastLength = useSelector((state: RootState) => state.tabGroup.past.length);

  const [revertStates, setRevertStates] = useState<Record<string, RevertState>>({});
  const [toastMessage, setToastMessage] = useState<AiChatMessage | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const fadeTimerRef = useRef<number | null>(null);
  const lastSeenAssistantId = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const hoverRef = useRef(false);
  const [isFading, setIsFading] = useState(false);

  const { messages, sendAudio, setMessages, isStreaming, reportError } = useAiChat({
    conversationId: null,
    contextMessage: workspaceContext?.message ?? null,
    metadata: workspaceContext?.metadata ?? null,
    tabSnapshot: workspaceContext?.tabSnapshot ?? null,
  });

  const lastAssistantMessage = useMemo(() => {
    const assistantMessages = messages.filter((m) => m.role === "assistant");
    return assistantMessages[assistantMessages.length - 1] ?? null;
  }, [messages]);

  useEffect(() => {
    if (!initializedRef.current) {
      lastSeenAssistantId.current = lastAssistantMessage?.id ?? null;
      initializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatVisible && lastAssistantMessage) {
      lastSeenAssistantId.current = lastAssistantMessage.id;
    }
  }, [chatVisible, lastAssistantMessage]);

  // Toast handling
  useEffect(() => {
    if (chatVisible) {
      setIsFading(false);
      setToastMessage(null);
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      return;
    }
    if (lastAssistantMessage) {
      if (lastAssistantMessage.id === lastSeenAssistantId.current) {
        return;
      }
      setToastMessage(lastAssistantMessage);
      setIsFading(false);
      lastSeenAssistantId.current = lastAssistantMessage.id;
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      toastTimerRef.current = window.setTimeout(() => {
        if (!hoverRef.current) {
          setIsFading(true);
          fadeTimerRef.current = window.setTimeout(() => {
            setToastMessage(null);
            setIsFading(false);
            fadeTimerRef.current = null;
          }, TOAST_FADE_MS);
        }
        toastTimerRef.current = null;
      }, TOAST_AUTO_HIDE_MS);
    }
  }, [chatVisible, lastAssistantMessage]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleSessionUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail as { conversationId: string | null; messages: AiChatMessage[] };
      if (detail && Array.isArray(detail.messages)) {
        setMessages(detail.messages);
      }
    };
    window.addEventListener("aiChatSessionUpdated", handleSessionUpdate);
    return () => {
      window.removeEventListener("aiChatSessionUpdated", handleSessionUpdate);
    };
  }, [setMessages]);

  // Voice capture
  const {
    start: startRecording,
    stop: stopRecording,
    abort: abortRecording,
  } = useVoiceRecorder({
    disabled: isStreaming,
    onAudio: (audio) => {
      void sendAudio(audio);
    },
    onError: reportError,
    onRecordingChange: (recording) => dispatch(setAiChatRecording(recording)),
  });

  useEffect(() => {
    const handleStart = () => startRecording();
    const handleStop = () => stopRecording();
    document.addEventListener("ambientVoiceStart", handleStart);
    document.addEventListener("ambientVoiceStop", handleStop);
    return () => {
      document.removeEventListener("ambientVoiceStart", handleStart);
      document.removeEventListener("ambientVoiceStop", handleStop);
      abortRecording();
    };
  }, [abortRecording, startRecording, stopRecording]);

  // Revert handling (reuse logic)
  const handleRevert = useCallback(
    (messageId: string, undoIndex: number) => {
      const currentState = revertStates[messageId];
      if (currentState && currentState !== "idle") {
        return;
      }
      setRevertStates((prev) => ({ ...prev, [messageId]: "reverting" }));
      setMessages((prev: AiChatMessage[]) =>
        prev.map((msg: AiChatMessage) =>
          msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "reverting" } } : msg,
        ),
      );
      try {
        dispatch(ActionCreators.jumpToPast(undoIndex));
        setRevertStates((prev) => ({ ...prev, [messageId]: "done" }));
        setMessages((prev: AiChatMessage[]) =>
          prev.map((msg: AiChatMessage) =>
            msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "done" } } : msg,
          ),
        );
      } catch (err) {
        console.error("Revert failed", err);
        setRevertStates((prev) => ({ ...prev, [messageId]: "error" }));
        setMessages((prev: AiChatMessage[]) =>
          prev.map((msg: AiChatMessage) =>
            msg.id === messageId ? { ...msg, meta: { ...msg.meta, revert_status: "error" } } : msg,
          ),
        );
      }
    },
    [dispatch, revertStates, setMessages],
  );

  if (!toastMessage || chatVisible) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={`ambient-chat-toast ${isFading ? "fading" : "visible"}`}>
      <div
        className="chat-content-container ambient-toast-content"
        onMouseEnter={() => {
          hoverRef.current = true;
          if (toastTimerRef.current) {
            window.clearTimeout(toastTimerRef.current);
            toastTimerRef.current = null;
          }
          if (fadeTimerRef.current) {
            window.clearTimeout(fadeTimerRef.current);
            fadeTimerRef.current = null;
          }
          setIsFading(false);
        }}
        onMouseLeave={() => {
          hoverRef.current = false;
          if (!toastTimerRef.current) {
            toastTimerRef.current = window.setTimeout(() => {
              if (!hoverRef.current) {
                setIsFading(true);
                fadeTimerRef.current = window.setTimeout(() => {
                  setToastMessage(null);
                  setIsFading(false);
                  fadeTimerRef.current = null;
                }, TOAST_FADE_MS);
              }
              toastTimerRef.current = null;
            }, TOAST_HOVER_HIDE_DELAY_MS);
          }
        }}
      >
        {(() => {
          const revertUndoIndex = toastMessage.meta?.revertUndoIndex;
          const canRevert =
            toastMessage.role === "assistant" &&
            typeof revertUndoIndex === "number" &&
            Number.isFinite(revertUndoIndex) &&
            pastLength > revertUndoIndex;
          const revertState = revertStates[toastMessage.id] ?? toastMessage.meta?.revert_status ?? "idle";
          return (
            <AiChatMessageRow
              message={toastMessage}
              onRevert={handleRevert}
              revertState={revertState}
              revertUndoIndex={revertUndoIndex}
              canRevert={canRevert}
            />
          );
        })()}
      </div>
    </div>,
    document.body,
  );
}
